import {
    Alert,
    Box,
    Button,
    Card,
    CardActionArea,
    CardContent,
    CardMedia,
    Container,
    Grid,
    Skeleton,
    Typography,
    useTheme,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import { motion } from 'framer-motion';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useMicrospecDynamicUIContext } from '../../../MicrospecDynamicUI/GlobalProvider';
import { orderBy as fbOrderBy } from 'firebase/firestore';
import { getDataFromCollectionWithQuery, getPaginatedData } from '../../../MicrospecDynamicUI/Helpers/dataUtils';
import { getLocalizationFromDescriptor } from '../../../MicrospecDynamicUI/Helpers/TextDescriptorUtils';
import { getOrDefault } from '../../../MicrospecDynamicUI/Helpers/Utils';
import { useMicrospecThemeLanguageContext } from '../../../MicrospecDynamicUI/Themes';
import { CardItem, FirebaseTitledCardsWithCTAButtonSectionProps, FirebaseTitledCardsWithCTAButtonSectionSpec } from './types';

const VIDEO_EXT_RE = /\.(mp4|webm|ogg|mov|avi|mkv)(\?|$)/i;
const isVideoUrl = (url: string) => VIDEO_EXT_RE.test(url);



const FirebaseTitledCardsWithCTAButtonSection: React.FC<FirebaseTitledCardsWithCTAButtonSectionProps> = ({
    spec,
    type,
    index,
}) => {
    const theme = useTheme();
    const { cloudProvider } = useMicrospecDynamicUIContext();
    const { localizationProvider } = useMicrospecThemeLanguageContext();

    const componentSpec = getOrDefault(spec, {}) as FirebaseTitledCardsWithCTAButtonSectionSpec;
    const { collectionPath, mapDocToCard } = componentSpec;

    const sectionId = getOrDefault(componentSpec.id, undefined);
    const backgroundColor = getOrDefault(componentSpec.backgroundColor, 'transparent');
    const accentColor = getOrDefault(componentSpec.accentColor, theme.palette.primary.main);
    const titleColor = getOrDefault(componentSpec.titleColor, theme.palette.text.primary);
    const limitCount: number = getOrDefault(componentSpec.limit, 0);
    const pageSize: number = getOrDefault(componentSpec.pageSize, 10);
    const actionButtonHref: string | undefined = getOrDefault(componentSpec.actionButtonHref, undefined);
    const actionButtonBackground = getOrDefault(componentSpec.actionButtonBackground, accentColor);
    const actionButtonTextColor = getOrDefault(
        componentSpec.actionButtonTextColor,
        theme.palette.primary.contrastText,
    );
    const rawQueryConstraints = componentSpec.queryConstraints;
    const queryConstraints: any[] = useMemo(() => rawQueryConstraints ?? [], [rawQueryConstraints]);
    const orderField: string | undefined = getOrDefault(componentSpec.orderField, undefined);
    const orderDirection: 'asc' | 'desc' = getOrDefault(componentSpec.orderDirection, 'asc');

    // For the limited (non-paginated) fetch, orderBy must be injected into the constraints.
    // For getPaginatedData it is passed as explicit params instead.
    const constraintsWithOrder: any[] = useMemo(
        () => (orderField ? [fbOrderBy(orderField, orderDirection), ...queryConstraints] : queryConstraints),
        [queryConstraints, orderField, orderDirection],
    );

    const emptyStateText = componentSpec.emptyStateText
        ? localizationProvider.translate(
              getLocalizationFromDescriptor(componentSpec.emptyStateText, { type: 'Literal', text: '' }),
          )
        : localizationProvider.translate({ type: 'Internal', text: 'noItemsToDisplay' });
    const emptyStateIcon = getOrDefault(componentSpec.emptyStateIcon, '📭');

    const sectionTitleText = componentSpec.sectionTitle
        ? localizationProvider.translate(
              getLocalizationFromDescriptor(componentSpec.sectionTitle, { type: 'Literal', text: '' }),
          )
        : '';

    const actionButtonLabel = componentSpec.actionButtonText
        ? localizationProvider.translate(
              getLocalizationFromDescriptor(componentSpec.actionButtonText, { type: 'Literal', text: '' }),
          )
        : '';

    const { firestore } = cloudProvider;

    const [cards, setCards] = useState<CardItem[]>([]);
    const [dataRetrieved, setDataRetrieved] = useState(false);
    const [fetchError, setFetchError] = useState<string | null>(null);
    const [lastDoc, setLastDoc] = useState<Record<string, any> | null>(null);
    const [hasMore, setHasMore] = useState(true);
    const [loadingMore, setLoadingMore] = useState(false);

    const sentinelRef = useRef<HTMLDivElement>(null);
    const isInfiniteScroll = !limitCount || limitCount === 0;

    // --- Initial fetch ---
    useEffect(() => {
        if (!firestore || !collectionPath) return;

        if (!isInfiniteScroll) {
            getDataFromCollectionWithQuery(
                (data: any[]) => {
                    setCards(data.map(mapDocToCard));
                },
                (retrieved: boolean) => {
                    setDataRetrieved(retrieved);
                },
                firestore.instance,
                collectionPath,
                constraintsWithOrder,
                false,
                limitCount,
            );
        } else {
            loadPage(null);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [firestore, collectionPath, queryConstraints]);

    const loadPage = useCallback(
        async (cursor: Record<string, any> | null) => {
            if (!firestore || !collectionPath) return;
            if (cursor !== null) setLoadingMore(true);

            let results: Record<string, any>[] = [];
            try {
                results = (await getPaginatedData(
                    cursor,
                    pageSize,
                    collectionPath,
                    firestore,
                    orderDirection,
                    orderField as any,
                    queryConstraints,
                )) as Record<string, any>[];
                setFetchError(null);
            } catch (err: any) {
                console.error('[FirebaseTitledCardsWithCTAButtonSection] Failed to load items:', err);
                setFetchError(err?.message ?? 'Failed to load items');
                setDataRetrieved(true);
                setLoadingMore(false);
                return;
            }

            const mapped = (results as any[]).map(mapDocToCard) as CardItem[];

            if (cursor === null) {
                setCards(mapped);
                setDataRetrieved(true);
            } else {
                setCards((prev) => [...prev, ...mapped]);
                setLoadingMore(false);
            }

            if (results.length < pageSize) {
                setHasMore(false);
            } else {
                setLastDoc(results[results.length - 1] ?? null);
            }
        },
        [firestore, collectionPath, pageSize, mapDocToCard, queryConstraints, orderField, orderDirection],
    );

    // --- Infinite scroll sentinel ---
    useEffect(() => {
        if (!isInfiniteScroll || !hasMore || !dataRetrieved) return undefined;

        const observer = new IntersectionObserver(
            (entries) => {
                if (entries[0].isIntersecting && !loadingMore) {
                    loadPage(lastDoc);
                }
            },
            { threshold: 0.1 },
        );

        const sentinel = sentinelRef.current;
        if (sentinel) observer.observe(sentinel);

        return () => {
            if (sentinel) observer.unobserve(sentinel);
        };
    }, [isInfiniteScroll, hasMore, dataRetrieved, lastDoc, loadingMore, loadPage]);

    // --- Skeleton placeholders ---
    const renderSkeletons = (count: number) =>
        Array.from({ length: count }).map((_, i) => (
            <Grid item xs={12} sm={6} md={4} lg={3} key={`skeleton-${i}`}>
                <Card
                    sx={{
                        borderRadius: 3,
                        overflow: 'hidden',
                        boxShadow: 'none',
                        border: `1px solid ${theme.palette.divider}`,
                    }}
                >
                    <Skeleton variant="rectangular" height={200} animation="wave" />
                    <CardContent>
                        <Skeleton variant="text" width="70%" height={28} animation="wave" />
                        <Skeleton variant="text" width="90%" height={20} animation="wave" />
                        <Skeleton variant="text" width="40%" height={20} animation="wave" />
                    </CardContent>
                </Card>
            </Grid>
        ));

    return (
        <Box
            id={sectionId}
            sx={{
                backgroundColor,
                py: { xs: 6, md: 8 },
                px: { xs: 2, sm: 3 },
            }}
        >
            <Container maxWidth="xl">
                {/* Section title */}
                {sectionTitleText && (
                    <motion.div
                        initial={{ opacity: 0, y: 24 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true }}
                        transition={{ duration: 0.55, ease: 'easeOut' }}
                    >
                        <Typography
                            variant="h3"
                            align="center"
                            sx={{
                                color: titleColor,
                                fontWeight: 800,
                                fontSize: { xs: '1.75rem', md: '2.25rem', lg: '2.75rem' },
                                mb: 1,
                                lineHeight: 1.2,
                            }}
                        >
                            {sectionTitleText}
                        </Typography>
                        <Box
                            sx={{
                                width: 56,
                                height: 4,
                                borderRadius: 2,
                                backgroundColor: accentColor,
                                mx: 'auto',
                                mb: { xs: 4, md: 6 },
                            }}
                        />
                    </motion.div>
                )}

                {/* Cards grid */}
                {fetchError && (
                    <Alert severity="error" sx={{ mb: 3 }}>
                        {fetchError}
                    </Alert>
                )}
                {!dataRetrieved ? (
                    <Grid container spacing={3}>
                        {renderSkeletons(pageSize || 8)}
                    </Grid>
                ) : cards.length === 0 && !fetchError ? (
                    <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.45, ease: 'easeOut' }}
                    >
                        <Box
                            sx={{
                                display: 'flex',
                                flexDirection: 'column',
                                alignItems: 'center',
                                justifyContent: 'center',
                                py: 8,
                                gap: 2,
                            }}
                        >
                            <Typography sx={{ fontSize: 64, lineHeight: 1 }}>{emptyStateIcon}</Typography>
                            <Typography
                                variant="h6"
                                sx={{
                                    color: theme.palette.text.secondary,
                                    fontWeight: 500,
                                    textAlign: 'center',
                                }}
                            >
                                {emptyStateText}
                            </Typography>
                        </Box>
                    </motion.div>
                ) : (
                    <Grid container spacing={3}>
                        {cards.map((card, i) => (
                            <Grid item xs={12} sm={6} md={4} lg={3} key={card.id ?? i}>
                                    <motion.div
                                        initial={{ opacity: 0, y: 40, scale: 0.96 }}
                                        whileInView={{ opacity: 1, y: 0, scale: 1 }}
                                        viewport={{ once: true, margin: '-60px' }}
                                        transition={{ type: 'spring', stiffness: 90, damping: 14, delay: Math.min(i % 10, 9) * 0.08 }}
                                        style={{ height: '100%' }}
                                    >
                                        <Card
                                            sx={{
                                                borderRadius: 3,
                                                overflow: 'hidden',
                                                height: '100%',
                                                display: 'flex',
                                                flexDirection: 'column',
                                                boxShadow: '0 2px 16px rgba(0,0,0,0.07)',
                                                border: `1px solid ${alpha(theme.palette.divider, 0.6)}`,
                                                transition: 'box-shadow 0.25s ease, transform 0.25s ease',
                                                '&:hover': {
                                                    boxShadow: `0 8px 32px ${alpha(accentColor, 0.22)}`,
                                                    transform: 'translateY(-4px)',
                                                },
                                            }}
                                        >
                                            <CardActionArea
                                                component={card.href ? 'a' : 'div'}
                                                href={card.href}
                                                sx={{
                                                    flexGrow: 1,
                                                    display: 'flex',
                                                    flexDirection: 'column',
                                                    alignItems: 'stretch',
                                                }}
                                            >
                                                {/* Image / Video / colored placeholder */}
                                                {(() => {
                                                    const src = card.mediaUrl || card.image;
                                                    if (src && isVideoUrl(src)) {
                                                        return (
                                                            <Box sx={{ position: 'relative', height: 200, overflow: 'hidden', bgcolor: '#000' }}>
                                                                <Box
                                                                    component="video"
                                                                    src={src}
                                                                    muted
                                                                    loop
                                                                    playsInline
                                                                    onMouseEnter={(e: React.MouseEvent<HTMLVideoElement>) => (e.currentTarget as HTMLVideoElement).play()}
                                                                    onMouseLeave={(e: React.MouseEvent<HTMLVideoElement>) => { const v = e.currentTarget as HTMLVideoElement; v.pause(); v.currentTime = 0; }}
                                                                    sx={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }}
                                                                />
                                                                {/* Play indicator */}
                                                                <Box sx={{
                                                                    position: 'absolute', inset: 0,
                                                                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                                                                    pointerEvents: 'none',
                                                                    background: 'linear-gradient(to top, rgba(0,0,0,0.35) 0%, transparent 60%)',
                                                                }}>
                                                                    <Box sx={{
                                                                        width: 44, height: 44, borderRadius: '50%',
                                                                        background: 'rgba(255,255,255,0.22)',
                                                                        backdropFilter: 'blur(4px)',
                                                                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                                                                        border: '1.5px solid rgba(255,255,255,0.5)',
                                                                    }}>
                                                                        <Typography sx={{ fontSize: 18, lineHeight: 1, ml: '3px' }}>▶</Typography>
                                                                    </Box>
                                                                </Box>
                                                            </Box>
                                                        );
                                                    }
                                                    if (src) {
                                                        return (
                                                            <CardMedia
                                                                component="img"
                                                                image={src}
                                                                alt={card.title}
                                                                sx={{ height: 200, objectFit: 'cover' }}
                                                            />
                                                        );
                                                    }
                                                    return (
                                                        <Box
                                                            sx={{
                                                                height: 200,
                                                                background: `linear-gradient(135deg, ${alpha(accentColor, 0.18)} 0%, ${alpha(accentColor, 0.4)} 100%)`,
                                                                display: 'flex',
                                                                alignItems: 'center',
                                                                justifyContent: 'center',
                                                            }}
                                                        >
                                                            {card.icon ? (
                                                                <Typography sx={{ fontSize: 64 }}>{card.icon}</Typography>
                                                            ) : (
                                                                <Box
                                                                    sx={{
                                                                        width: 64,
                                                                        height: 64,
                                                                        borderRadius: '50%',
                                                                        backgroundColor: alpha(accentColor, 0.3),
                                                                        border: `2px solid ${alpha(accentColor, 0.5)}`,
                                                                    }}
                                                                />
                                                            )}
                                                        </Box>
                                                    );
                                                })()}

                                                {/* Title */}
                                                <CardContent sx={{ flexGrow: 1 }}>
                                                    <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 1 }}>
                                                        <Typography
                                                            variant="h6"
                                                            sx={{
                                                                fontWeight: 700,
                                                                fontSize: { xs: '1rem', md: '1.1rem' },
                                                                color: theme.palette.text.primary,
                                                                lineHeight: 1.4,
                                                                display: '-webkit-box',
                                                                WebkitLineClamp: 2,
                                                                WebkitBoxOrient: 'vertical',
                                                                overflow: 'hidden',
                                                            }}
                                                        >
                                                            {card.title}
                                                        </Typography>
                                                        {card.secondarySubtitle && (
                                                            <Typography
                                                                variant="subtitle2"
                                                                sx={{
                                                                    fontWeight: 700,
                                                                    color: accentColor,
                                                                    whiteSpace: 'nowrap',
                                                                    flexShrink: 0,
                                                                }}
                                                            >
                                                                {card.secondarySubtitle}
                                                            </Typography>
                                                        )}
                                                    </Box>
                                                    {card.primarySubtitle && (
                                                        <Typography
                                                            variant="body2"
                                                            sx={{
                                                                mt: 1,
                                                                color: theme.palette.text.secondary,
                                                                display: '-webkit-box',
                                                                WebkitLineClamp: 2,
                                                                WebkitBoxOrient: 'vertical',
                                                                overflow: 'hidden',
                                                            }}
                                                        >
                                                            {card.primarySubtitle}
                                                        </Typography>
                                                    )}
                                                </CardContent>
                                            </CardActionArea>
                                            {card.actionButton && (
                                                <Box sx={{ px: 2, pb: 2 }}>
                                                    <Button
                                                        component="a"
                                                        href={card.actionButton.href}
                                                        variant="outlined"
                                                        fullWidth
                                                        size="small"
                                                        sx={{
                                                            borderRadius: 2,
                                                            fontWeight: 600,
                                                            textTransform: 'none',
                                                            borderColor: accentColor,
                                                            color: accentColor,
                                                            '&:hover': {
                                                                borderColor: accentColor,
                                                                backgroundColor: alpha(accentColor, 0.08),
                                                            },
                                                        }}
                                                    >
                                                        {localizationProvider.translate(card.actionButton.label) ?? card.actionButton.label}
                                                    </Button>
                                                </Box>
                                            )}
                                        </Card>
                                    </motion.div>
                            </Grid>
                        ))}

                        {/* Skeleton rows for loading-more state */}
                        {loadingMore && renderSkeletons(pageSize)}
                    </Grid>
                )}

                {/* Infinite scroll sentinel */}
                {isInfiniteScroll && hasMore && dataRetrieved && (
                    <Box ref={sentinelRef} sx={{ height: 40, mt: 2 }} />
                )}

                {/* Action button */}
                {actionButtonLabel && actionButtonHref && (
                    <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true }}
                        transition={{ duration: 0.5, delay: 0.2 }}
                    >
                        <Box sx={{ textAlign: 'center', mt: { xs: 5, md: 7 } }}>
                            <Button
                                component="a"
                                href={actionButtonHref}
                                variant="contained"
                                size="large"
                                sx={{
                                    backgroundColor: actionButtonBackground,
                                    color: actionButtonTextColor,
                                    borderRadius: 2,
                                    px: 5,
                                    py: 1.5,
                                    fontWeight: 700,
                                    fontSize: '1rem',
                                    textTransform: 'none',
                                    boxShadow: `0 4px 20px ${alpha(actionButtonBackground, 0.35)}`,
                                    '&:hover': {
                                        backgroundColor: actionButtonBackground,
                                        opacity: 0.9,
                                        boxShadow: `0 8px 28px ${alpha(actionButtonBackground, 0.45)}`,
                                    },
                                }}
                            >
                                {actionButtonLabel}
                            </Button>
                        </Box>
                    </motion.div>
                )}
            </Container>
        </Box>
    );
};

export default FirebaseTitledCardsWithCTAButtonSection;
